<template>
  <DialogWrapper
    close-on-cancel
    class="invite-onboarding-questionnaire-dialog"
    saveBtnLabelI18nKey="common.btn.submit"
    :show.sync="show"
    :loading="loading"
    :titleI18nKey="`${i18nPrefix}.title`"
    @save="handleSave"
  >
    <WrappedForm
      ref="form"
      :model="invitation"
      :formLines="formLines"
    >
      <template #notificationEmail>
        <div class="email-input-container">
          <el-input
            v-model="email"
            class="email-input"
            :class="emailErrorMessage && 'error'"
            @keypress.enter.native="handleAddEmail"
          ></el-input>
          <div class="add-btn" @click="handleAddEmail">+ {{ $t(`${i18nPrefix}.btn.addEmail`) }}</div>
          <div v-if="emailErrorMessage" class="error-message">{{ emailErrorMessage }}</div>
        </div>
        <div v-show="emailList && emailList.length > 0" class="email-list">
          <div
            v-for="(email, index) in emailList"
            class="list-item"
            :key="index"
          >
            <span class="email">{{ email }}</span>
            <div class="delete-btn" @click="handelDeleteEmail(email, index)">
              <i class="btn-icon el-icon-close"></i>
            </div>
          </div>
        </div>
      </template>
    </WrappedForm>
  </DialogWrapper>
</template>

<script>
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import { mapActions } from 'vuex'
import { validateEmail } from '@/utils/validator'
import _ from 'lodash'
import moment from 'moment'

export default {
  components: {
    DialogWrapper,
    WrappedForm
  },
  props: {
    jobId: {
      required: false
    },
    isAllApplication: {
      default: false
    },
    searchParams: {
      required: false
    },
    applicationList: {
      required: true
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.invitation.onboardingQuestionnaire',
      show: false,
      loading: false,
      questionnaireList: [],
      inductionPageList: [],
      loader: null,
      invitation: {},
      email: null,
      emailErrorMessage: null
    }
  },
  computed: {
    formLines () {
      const i18nPrefix = this.i18nPrefix
      const lines = {
        inductionPage: {
          elType: 'el-select',
          label: this.$t(`${i18nPrefix}.label.inductionPage`),
          option: {
            data: this.inductionPageList,
            labelKey: 'name',
            valueKey: 'id'
          },
          props: { filterable: true, clearable: true },
          rules: [{ validator: this.validateInductionPage, trigger: 'blur,change' }]
        },
        questionnaire: {
          elType: 'el-select',
          label: this.$t(`${i18nPrefix}.label.questionnaire`),
          option: {
            data: this.questionnaireList,
            labelKey: 'name',
            valueKey: 'id'
          },
          props: { filterable: true },
          rules: [{ validator: this.validateQuestionnaire, trigger: 'blur,change' }]
        },
        onboardDate: {
          elType: 'el-date-picker',
          label: this.$t(`${i18nPrefix}.label.onboardDate`),
          props: { clearable: true }
        },
        notificationEmail: {
          contentSlot: 'notificationEmail',
          label: this.$t(`${i18nPrefix}.label.notificationEmail`)
        }
      }
      return lines
    },
    form () {
      return this.$refs.form
    },
    emailList () {
      return this.invitation.notificationEmail || []
    }
  },
  created () {

  },
  methods: {
    ...mapActions('onboardingManagement', ['searchQuestionnaire', 'searchMicroSite', 'createInvitation']),
    openDialog () {
      this.show = true
    },
    clearForm () {
      this.invitation = {}
      this.$nextTick(() => {
        this.form.clearValidate()
        this.email = null
        this.emailErrorMessage = null
      })
    },
    async loadData () {
      this.loading = true
      if (!this.loader) {
        this.loader = Promise.all([
          this.searchQuestionnaire({ just_actived_list: true }).then(resp => {
            this.questionnaireList = resp.questionnaires
          }),
          this.searchMicroSite({ just_actived_list: true }).then(resp => {
            this.inductionPageList = resp.pages
          })
        ])
      }
      await this.loader
      this.loading = false
    },
    validateInductionPage (rule, value, callback) {
      if (!value && !this.invitation.questionnaire) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.atLeastOneInductionPageOrQuestionnaire`)))
        return
      }
      callback()
    },
    validateQuestionnaire (rule, value, callback) {
      if (!value && !this.invitation.inductionPage) {
        callback(new Error(this.$t(`${this.i18nPrefix}.message.atLeastOneInductionPageOrQuestionnaire`)))
        return
      }
      callback()
    },
    handleAddEmail () {
      const email = this.email
      this.emailErrorMessage = null
      const error = validateEmail(null, email)
      if (error) {
        this.emailErrorMessage = error
        return
      }
      const found = _.includes(this.invitation.notificationEmail, email)
      if (found) {
        this.emailErrorMessage = this.$t(`${this.i18nPrefix}.message.emailHasBeenUsed`)
        return
      }
      if (this.invitation.notificationEmail) {
        this.invitation.notificationEmail.push(email)
      } else {
        this.$set(this.invitation, 'notificationEmail', [email])
      }
      this.email = null
    },
    handelDeleteEmail (email, index) {
      this.invitation.notificationEmail.splice(index, 1)
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      const isValid = await this.form.submit()
      if (!isValid) {
        return
      }
      this.loading = true
      const invitation = {
        is_all_application: this.isAllApplication,
        search_params: this.searchParams,
        candidate_ids: _.map(this.applicationList, item => item.candidate.id),
        job_id: this.jobId,
        page_id: this.invitation.inductionPage,
        questionnaire_id: this.invitation.questionnaire,
        notification_email: this.invitation.notificationEmail,
        onboarding_date: moment(this.invitation.onboardDate).format('YYYY-MM-DD')
      }
      const resp = await this.createInvitation(invitation)
      this.loading = false
      if (resp) {
        if (resp.has_already_invited_ids.length > 0 || resp.invalid_invitation.length > 0) {
          // this.$message.success(this.$t(`${this.i18nPrefix}.message.hasInvited`))
          this.$message.warning(resp.message)
        } else {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.inviteSuccess`))
        }
        this.show = false
        _.forEach(this.applicationList, application => {
          if (_.includes(resp.new_invited_candidate_ids, application.candidate.id)) {
            application.status = {
              key: 'Onboarding Form Invited',
              name_cn: '入职问卷已邀请',
              name_en: 'Onboarding Form Invited'
            }
          }
        })
        this.$emit('invited', resp)
      } else {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.inviteFail`))
      }
    }
  },
  watch: {
    show () {
      if (this.show) {
        this.clearForm()
        this.loadData()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.invite-onboarding-questionnaire-dialog {
  .email-input-container {
    .email-input {
      width: 75%;

      &.error {
        ::v-deep {
          .el-input__inner {
            border-color: #f56c6c;
          }
        }
      }
    }

    .add-btn {
      display: inline-block;
      width: 25%;
      text-align: right;
      cursor: pointer;
    }

    .error-message {
      color: #f56c6c;
    }
  }

  .email-list {
    width: 75%;

    .list-item {
      display: flex;

      .email {
        flex: 1;
      }

      .delete-btn {
        margin-left: 10px;
        margin-right: 0;
        font-size: 18px;
        cursor: pointer;

        .btn-icon {
          transition: transform .3s;

          &:hover {
            transform: rotateZ(180deg);
          }
        }
      }
    }
  }
}
</style>
